import React, { useState, useContext, useEffect, useCallback } from "react"
import { useHistory } from "react-router-dom"
import { AuthContext } from "../../utils/providers/auth"
import { ModalContext } from "../../utils/providers/modal"
import { PlusIcon, SearchIcon } from "../../utils/svgs"
import { auth, db } from "../../utils/firebase"
import firebase from "firebase"
import "./enquiries.scss"

/**
 * UI components
 */
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Input from "../../components/ui/inputs/input"
import Table from "../../components/structure/table/table"
import Navigation from "../../components/ui/navigation/navigation"
import RequireAttention from "./types/require-attention"
import ReturnTravellers from "./types/return-travellers"
import Archive from "./types/archive"
import Enquiry from "../../components/enquiry/enquiry"
import Button from "../../components/ui/button/button"
import Checkbox from "../../components/ui/checkbox/checkbox"
import Window from "../../components/structure/window/window"
import Add from "./add/add"
import Assign from "./assign/assign"
import { normaliseTag } from "../../utils/tags"
import { enquiryMatchesSearch } from "../../utils/enquiries/search"
import { readTextToParse } from "../../utils/enquiries/to-parse"
import {
	readStoredSort,
	writeStoredSort,
	sortByCreatedDesc,
	getQueueSortValue,
	sortItems,
	nextSortState,
} from "../../utils/enquiries/sort"

const QUEUE_STATUSES = ["FREEPHONE", "HOT", "BEING_PROCESSED"]
const CUSTOM_TAB_PREFIX = "CUSTOM_TAB:"
const QUEUE_SORT_STORAGE_KEY = "enquiries.queueSort"
const ARCHIVE_SORT_STORAGE_KEY = "enquiries.archiveSort"

const queueLabel = {
	BEING_PROCESSED: "Processing",
	HOT: "Hot",
	FREEPHONE: "Freephone",
}

function CustomTabWindow({ uid, availableTags, existingTabs, editingTab, tabCount, close, onSaved, onDeleted }) {
	const [label, setLabel] = useState(editingTab?.label || "")
	const [selectedTags, setSelectedTags] = useState(editingTab?.tags || [])
	const [draft, setDraft] = useState("")
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState("")
	const { showModal } = useContext(ModalContext)

	const addTag = (raw) => {
		const tag = normaliseTag(raw)
		if (!tag || selectedTags.includes(tag)) return
		setSelectedTags([...selectedTags, tag])
		setDraft("")
		setError("")
	}

	const removeTag = (tag) => {
		setSelectedTags(selectedTags.filter((selectedTag) => selectedTag !== tag))
	}

	const saveTab = async () => {
		if (!uid) {
			setError("You need to be signed in to save tabs.")
			return
		}
		const tabLabel = label.trim()
		if (!tabLabel) {
			setError("Give this tab a name.")
			return
		}
		if (selectedTags.length === 0) {
			return
		}

		setSaving(true)
		const tabID = editingTab?.id || db.collection("users").doc().id
		const existingTab = existingTabs.find((tab) => tab.id === tabID)
		const tabData = {
			id: tabID,
			label: tabLabel,
			tags: selectedTags,
			match: "all",
			order: editingTab?.order || tabCount + 1,
			updated: firebase.firestore.Timestamp.now(),
		}

		if (!editingTab?.id) {
			tabData.created = firebase.firestore.Timestamp.now()
		} else if (existingTab?.created) {
			tabData.created = existingTab.created
		}

		try {
			const nextTabs = existingTabs
				.filter((tab) => tab.id !== tabID)
				.concat(tabData)
				.sort((a, b) => (a.order || 0) - (b.order || 0))
			await db.doc(`users/${uid}`).set({ enquiry_tabs: nextTabs }, { merge: true })
			setSaving(false)
			onSaved(tabID)
		} catch (err) {
			console.error("Unable to save enquiry tab", err)
			setSaving(false)
			setError(err?.message || "This tab could not be saved. Please try again.")
		}
	}

	const deleteTab = async () => {
		if (!editingTab?.id) return
		setSaving(true)
		try {
			await db.doc(`users/${uid}`).set(
				{ enquiry_tabs: existingTabs.filter((tab) => tab.id !== editingTab.id) },
				{ merge: true }
			)
			setSaving(false)
			onDeleted(editingTab.id)
		} catch (err) {
			console.error("Unable to delete enquiry tab", err)
			setSaving(false)
			setError(err?.message || "This tab could not be deleted. Please try again.")
		}
	}

	const confirmDeleteTab = () => {
		if (!editingTab?.id) return
		showModal({
			type: "ALERT",
			title: "Delete filtered tab?",
			body: `This removes the "${editingTab.label}" tab only. Tags and enquiries will not be changed.`,
			cancel: {
				label: "Cancel",
				action: () => null,
			},
			next: {
				label: "Delete tab",
				action: deleteTab,
			},
		})
	}

	const suggestions = draft
		? availableTags.filter((tag) => tag.includes(normaliseTag(draft)) && !selectedTags.includes(tag)).slice(0, 8)
		: availableTags.filter((tag) => !selectedTags.includes(tag)).slice(0, 8)

	return (
		<Window
			title={editingTab ? "Edit filtered tab" : "New filtered tab"}
			subtitle="Tabs show enquiries that match all selected tags."
			close={close}
			className="slim enquiry-tab-window">
			<div className="enquiry-tab-form">
				<Input
					label="Tab name"
					showLabel={true}
					value={label}
					placeholder="Tab name"
					onChange={(value) => setLabel(value)}
				/>

				<div className="enquiry-tab-tags-field">
					<label>Filter tags</label>
					<div className="enquiry-tab-tag-row">
						{selectedTags.length === 0 && <small className="enquiry-tab-placeholder">No tags selected</small>}
						{selectedTags.map((tag) => (
							<span
								className="enquiry-tab-tag"
								key={tag}>
								{tag}
								<button
									type="button"
									onClick={() => removeTag(tag)}>
									×
								</button>
							</span>
						))}
					</div>
				</div>

				<div className="enquiry-tab-add-tag">
					<Input
						value={draft}
						placeholder="Type or choose a tag"
						onChange={(value) => setDraft(value)}
						onKeyPress={(e) => {
							if (e.key === "Enter") {
								e.preventDefault()
								addTag(draft)
							}
						}}
					/>
					<Button
						small={true}
						className="enquiry-tab-add-tag-button"
						icon={<PlusIcon />}
						onClick={() => addTag(draft)}
					/>
				</div>

				{suggestions.length > 0 && (
					<div className="enquiry-tab-suggestions-field">
						<label>Your tags</label>
						<div className="enquiry-tab-suggestions">
							{suggestions.map((tag) => (
								<button
									key={tag}
									type="button"
									onClick={() => addTag(tag)}>
									<span>+</span>
									{tag}
								</button>
							))}
						</div>
					</div>
				)}

				{error && <small className="enquiry-tab-error">{error}</small>}

				<div className="ui-window-actions">
					<div>
						{editingTab && (
							<Button
								type="OUTLINE"
								small={true}
								label="Delete tab"
								disabled={saving}
								onClick={confirmDeleteTab}
							/>
						)}
					</div>
					<Button
						small={true}
						label={editingTab ? "Save tab" : "Create tab"}
						loading={saving}
						disabled={saving || selectedTags.length === 0}
						onClick={saveTab}
					/>
				</div>
			</div>
		</Window>
	)
}

/**
 * Functional component to return the enquiry tables
 */
function Enquiries() {
	const [activeTab, setActiveTab] = useState("BEING_PROCESSED")
	const [requireAttentionCount, setRequireAttentionCount] = useState(0)
	const [returnTravellersCount, setReturnTravellersCount] = useState(0)
	const [customTabs, setCustomTabs] = useState([])
	const [allEnquiries, setAllEnquiries] = useState([])
	const [clientsById, setClientsById] = useState({})
	const [loadingEnquiries, setLoadingEnquiries] = useState(false)
	const [addingEnquiry, setAddingEnquiry] = useState(false)
	const [assignEnquiry, setAssignEnquiry] = useState("")
	const [bulkAssignments, setBulkAssignments] = useState([])
	const [bulkAssigning, setBulkAssigning] = useState(false)
	const [queueSelection, setQueueSelection] = useState([])
	const [editingCustomTab, setEditingCustomTab] = useState(null)
	const [archiveEmailDraft, setArchiveEmailDraft] = useState("")
	const [archiveEmailSearch, setArchiveEmailSearch] = useState("")
	const [archiveSearching, setArchiveSearching] = useState(false)
	const [enquirySearch, setEnquirySearch] = useState("")
	const [queueSort, setQueueSort] = useState(() => readStoredSort(QUEUE_SORT_STORAGE_KEY))
	const [archiveSort, setArchiveSort] = useState(() => readStoredSort(ARCHIVE_SORT_STORAGE_KEY))

	useEffect(() => {
		writeStoredSort(QUEUE_SORT_STORAGE_KEY, queueSort)
	}, [queueSort])

	useEffect(() => {
		writeStoredSort(ARCHIVE_SORT_STORAGE_KEY, archiveSort)
	}, [archiveSort])

	/**
	 * Pull the current user's object in from the auth context
	 */
	const { user } = useContext(AuthContext)
	const { showModal } = useContext(ModalContext)
	const history = useHistory()
	// Stable reference so memoised Enquiry rows don't re-render on every keystroke
	// or selection change just because this callback was recreated.
	const openEnquiry = useCallback((enquiryID) => history.push(`/enquiry/${enquiryID}`), [history])
	const uid = auth.currentUser?.uid

	/**
	 * Who can act on a multi-selection in the main queue: anyone who can assign
	 * enquiries may reassign them, super admins may also delete. The select
	 * column and bulk bar only appear when the user can do at least one.
	 */
	const canBulkReassign = Boolean(user?.show_for?.can_assign)
	const canBulkDelete = Boolean(user?.is_super_admin)
	const canBulkSelect = canBulkReassign || canBulkDelete
	const activeCustomTabID = activeTab.startsWith(CUSTOM_TAB_PREFIX) ? activeTab.replace(CUSTOM_TAB_PREFIX, "") : ""
	const activeCustomTab = customTabs.find((tab) => tab.id === activeCustomTabID)

	// On component load
	useEffect(() => {
		// Check if we have a to_parse parameter, pre-load the add enquiry window
		if (readTextToParse()) setAddingEnquiry(true)
	}, [])

	/**
	 * Load the current user's tag library and saved custom tabs.
	 */
	useEffect(() => {
		if (!uid) return

		const unsubscribeUser = db.doc(`users/${uid}`).onSnapshot((doc) => {
			if (!doc.exists) return
			const userData = doc.data()
			const tabs = (userData.enquiry_tabs || []).sort((a, b) => (a.order || 0) - (b.order || 0))
			setCustomTabs(tabs)
		})

		return () => {
			unsubscribeUser()
		}
	}, [uid])

	/**
	 * Keep the user's open assigned queue enquiries in memory, then derive tabs locally.
	 */
	useEffect(() => {
		if (!uid || !user) return

		setLoadingEnquiries(true)
		setAllEnquiries([])

		const unsubscribers = QUEUE_STATUSES.map((status) =>
			db
				.collection("enquiries")
				.where("agent", "==", uid)
				.where("status", "==", status)
				.onSnapshot((enquiriesSnap) => {
					const nextEnquiries = enquiriesSnap.docs
						.map((doc) => ({ id: doc.id, ...doc.data() }))
					setAllEnquiries((currentEnquiries) => {
						const otherStatuses = currentEnquiries.filter((enquiry) => enquiry.status !== status)
						return [...otherStatuses, ...nextEnquiries].sort(sortByCreatedDesc)
					})
					setLoadingEnquiries(false)
				}, (err) => {
					console.error(`Unable to load ${status} enquiries`, err)
					setAllEnquiries((currentEnquiries) => currentEnquiries.filter((enquiry) => enquiry.status !== status))
					setLoadingEnquiries(false)
				})
		)

		return () => unsubscribers.forEach((unsubscribe) => unsubscribe())
	}, [uid, user])

	/**
	 * Preload client records for visible open enquiries so rows do not hydrate
	 * cell-by-cell whenever a tab changes.
	 */
	useEffect(() => {
		const missingClientIds = Array.from(
			new Set(
				allEnquiries
					.map((enquiry) => enquiry.client)
					.filter((clientId) => clientId && !clientsById[clientId])
			)
		)
		if (missingClientIds.length === 0) return

		let isMounted = true
		const loadClients = async () => {
			const clientData = {}
			const batchSize = 10
			for (let i = 0; i < missingClientIds.length; i += batchSize) {
				const batch = missingClientIds.slice(i, i + batchSize)
				const clientDocs = await db
					.collection("clients")
					.where(firebase.firestore.FieldPath.documentId(), "in", batch)
					.get()
				clientDocs.forEach((doc) => {
					clientData[doc.id] = doc.data()
				})
			}
			if (!isMounted) return
			setClientsById((currentClients) => ({
				...currentClients,
				...clientData,
			}))
		}

		loadClients().catch((err) => console.error("Unable to load enquiry clients", err))
		return () => {
			isMounted = false
		}
	}, [allEnquiries, clientsById])

	useEffect(() => {
		if (!activeCustomTabID) return
		if (customTabs.length > 0 && !customTabs.some((tab) => tab.id === activeCustomTabID)) {
			setActiveTab("BEING_PROCESSED")
		}
	}, [activeCustomTabID, customTabs])

	/**
	 * Drop any selected IDs that have left the live queue (reassigned away,
	 * deleted, or otherwise moved out of the user's open statuses). This also
	 * clears the selection automatically once a bulk action completes.
	 */
	useEffect(() => {
		setQueueSelection((current) => {
			if (current.length === 0) return current
			const presentIds = new Set(allEnquiries.map((enquiry) => enquiry.id))
			const next = current.filter((id) => presentIds.has(id))
			return next.length === current.length ? current : next
		})
	}, [allEnquiries])

	const searchActive = enquirySearch.trim().length > 0
	const searchedEnquiries = allEnquiries.filter((enquiry) =>
		enquiryMatchesSearch(enquiry, clientsById[enquiry.client], enquirySearch)
	)
	const queueEnquiries = searchActive ? searchedEnquiries : allEnquiries

	const customNavigation = customTabs.map((tab) => ({
		id: `${CUSTOM_TAB_PREFIX}${tab.id}`,
		label: tab.label,
		count: queueEnquiries.filter((enquiry) => {
			const tags = enquiry.tags || []
			return (tab.tags || []).every((tag) => tags.includes(tag))
		}).length,
		actions: [
			{
				label: "Edit tab",
				onClick: () => setEditingCustomTab(tab),
			},
		],
	}))

	const fullNavigation = [
		{
			id: "REQUIRE_ATTENTION",
			label: "Require attention",
			count: requireAttentionCount,
		},
		{
			id: "RETURN_TRAVELLERS",
			label: "Return Travellers",
			count: returnTravellersCount,
		},
		{
			id: "STATUS_LABEL",
			label: "Status",
			divider: true,
		},
		{
			id: "BEING_PROCESSED",
			label: "Processing",
			count: queueEnquiries.filter((enquiry) => enquiry.status === "BEING_PROCESSED").length,
		},
		{
			id: "HOT",
			label: "Hot",
			count: queueEnquiries.filter((enquiry) => enquiry.status === "HOT").length,
		},
		{
			id: "FREEPHONE",
			label: "Freephone",
			count: queueEnquiries.filter((enquiry) => enquiry.status === "FREEPHONE").length,
		},
		...(customNavigation.length > 0 ? [{ id: "CUSTOM_TAGS_LABEL", label: "Tags", divider: true, spaced: true }] : []),
		...customNavigation,
	]

	const restrictedNavigation = [
		{
			id: "RETURN_TRAVELLERS",
			label: "Return Travellers",
			count: returnTravellersCount,
		},
		{
			id: "BEING_PROCESSED",
			label: "Processing",
			count: queueEnquiries.filter((enquiry) => enquiry.status === "BEING_PROCESSED").length,
		},
		{
			id: "HOT",
			label: "Hot",
			count: queueEnquiries.filter((enquiry) => enquiry.status === "HOT").length,
		},
		{
			id: "FREEPHONE",
			label: "Freephone",
			count: queueEnquiries.filter((enquiry) => enquiry.status === "FREEPHONE").length,
		},
		...(customNavigation.length > 0 ? [{ id: "CUSTOM_TAGS_LABEL", label: "Tags", divider: true, spaced: true }] : []),
		...customNavigation,
	]

	const showQueueTable = activeTab !== "REQUIRE_ATTENTION" && activeTab !== "RETURN_TRAVELLERS" && activeTab !== "ARCHIVE"
	const usedTags = Array.from(
		new Set(allEnquiries.flatMap((enquiry) => enquiry.tags || []))
	).sort((a, b) => a.localeCompare(b))
	const unsortedEnquiries = activeCustomTab
		? queueEnquiries.filter((enquiry) => {
			const tags = enquiry.tags || []
			return (activeCustomTab.tags || []).every((tag) => tags.includes(tag))
		})
		: queueEnquiries.filter((enquiry) => enquiry.status === activeTab)
	const enquiries = sortItems(
		unsortedEnquiries,
		queueSort,
		(enquiry, key) => getQueueSortValue(enquiry, key, clientsById)
	)

	/**
	 * Multi-select helpers for the queue. Select-all only ever acts on the rows
	 * currently visible in the active tab, mirroring the Require attention tab.
	 */
	const visibleQueueIds = enquiries.map((enquiry) => enquiry.id)
	const selectedVisibleQueueCount = visibleQueueIds.filter((id) => queueSelection.includes(id)).length
	const allVisibleQueueSelected = visibleQueueIds.length > 0 && selectedVisibleQueueCount === visibleQueueIds.length

	const toggleQueueSelect = useCallback((enquiryID) => {
		setQueueSelection((current) =>
			current.includes(enquiryID)
				? current.filter((id) => id !== enquiryID)
				: [...current, enquiryID]
		)
	}, [])

	const toggleQueueSelectAll = () => {
		if (allVisibleQueueSelected) {
			setQueueSelection((current) => current.filter((id) => !visibleQueueIds.includes(id)))
		} else {
			setQueueSelection((current) => Array.from(new Set([...current, ...visibleQueueIds])))
		}
	}

	/**
	 * Hand the hand-picked selection to the existing Assign window (its bulk
	 * path assigns exactly the chosen enquiries).
	 */
	const bulkReassignSelected = () => {
		if (queueSelection.length === 0) return
		setBulkAssignments(queueSelection)
		setBulkAssigning(true)
	}

	/**
	 * Permanently delete the selected enquiries (super admins only), detaching
	 * each from its client first, mirroring the per-row delete.
	 */
	const runBulkDelete = async () => {
		const ids = [...queueSelection]
		// Clear the selection up front; rows fall away as the delete lands.
		setQueueSelection([])
		try {
			/**
			 * Commit the deletes in batches (Firestore caps a batch at 500 writes)
			 * rather than firing hundreds of parallel writes - the parallel approach
			 * saturates the connection and re-renders the list on every individual
			 * delete. The enquiryDocumentDeleted trigger detaches each enquiry from
			 * its client and archives it into enquiries_removed, so we only need to
			 * delete the doc here (no client-side arrayRemove, which also avoids
			 * contention when several selected enquiries share the same client).
			 */
			const batchSize = 450
			for (let i = 0; i < ids.length; i += batchSize) {
				const batch = db.batch()
				ids.slice(i, i + batchSize).forEach((id) => batch.delete(db.doc(`enquiries/${id}`)))
				await batch.commit()
			}
		} catch (err) {
			console.error("Unable to delete selected enquiries", err)
		}
	}

	const bulkDeleteSelected = () => {
		if (queueSelection.length === 0) return
		const count = queueSelection.length
		showModal({
			type: "ALERT",
			title: `Delete ${count} ${count === 1 ? "enquiry" : "enquiries"}?`,
			body: "Once deleted, you won't be able to recover the data for these enquiries.",
			cancel: { label: "Cancel", action: () => null },
			next: { label: "Yes, delete", action: () => runBulkDelete() },
		})
	}

	const emptyQueueMessage = activeCustomTab
		? searchActive
			? `No enquiries found for "${enquirySearch.trim()}" in "${activeCustomTab.label}".`
			: `No enquiries match every tag on "${activeCustomTab.label}".`
		: searchActive
			? `No enquiries found for "${enquirySearch.trim()}" in ${queueLabel[activeTab] || "this tab"}.`
			: `There are no enquiries in ${queueLabel[activeTab] || "this tab"}.`
	const queueHeadings = [
		{ key: "site", label: "Site" },
		{ key: "name", label: "Name" },
		{ key: "email", label: "Email" },
		{ key: "telephone", label: "Telephone" },
		{ key: "follow_up", label: "Follow up" },
		{ key: "location", label: "Location/Event" },
		{ key: "date_from", label: "Date from" },
		{ key: "group_size", label: "Group size" },
		{ key: "quoted", label: "Quoted" },
		{ key: "age", label: "Age" },
	].map((heading) => ({
		...heading,
		sortable: true,
		active: queueSort.key === heading.key,
		direction: queueSort.key === heading.key ? queueSort.direction : "",
		onClick: () => setQueueSort((currentSort) => nextSortState(currentSort, heading.key)),
	}))

	/**
	 * Leading checkbox column (select every visible row at once) when the user
	 * has a bulk capability.
	 */
	const queueTableHeadings = canBulkSelect
		? [
			{
				key: "select",
				sortable: false,
				label: (
					<Checkbox
						className="enquiry-select-all"
						checked={allVisibleQueueSelected}
						onClick={toggleQueueSelectAll}
					/>
				),
			},
			...queueHeadings,
		]
		: queueHeadings
	const queueColSpan = canBulkSelect ? 11 : 10

	const archiveHeadings = [
		{ key: "site", label: "Site" },
		{ key: "name", label: "Name" },
		{ key: "location", label: "Location/Event" },
		{ key: "date_from", label: "Date from" },
		{ key: "follow_up", label: "Follow up" },
		{ key: "group_size", label: "Group size" },
		{ key: "quoted", label: "Quoted" },
		{ key: "age", label: "Age" },
		{ key: "agent", label: "Archived by" },
		{ key: "archived_on", label: "Archived on" },
		{ key: "actions", label: "" },
	].map((heading) => ({
		...heading,
		sortable: heading.key !== "actions",
		active: archiveSort.key === heading.key,
		direction: archiveSort.key === heading.key ? archiveSort.direction : "",
		onClick: () => setArchiveSort((currentSort) => nextSortState(currentSort, heading.key)),
	}))

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>All enquiries</h1>

				<Input
					value={enquirySearch}
					icon={<SearchIcon />}
					placeholder="Quick search"
					onChange={(value) => setEnquirySearch(value)}
					wrapperClass="enquiry-quick-search"
				/>

				{/* New enquiry button */}
				<Button
					label="New enquiry"
					small={true}
					icon={<PlusIcon />}
					onClick={() => setAddingEnquiry(true)}
				/>
			</Title>

			<Navigation
				links={user?.show_for?.can_assign ? fullNavigation : restrictedNavigation}
				active={activeTab}
				onClick={(tabID) => setActiveTab(tabID)}>
				<Button
					label="View archived"
					type="OUTLINE"
					xsSmall={true}
					onClick={() => setActiveTab("ARCHIVE")}
				/>
				<Button
					label="New tab"
					xsSmall={true}
					icon={<PlusIcon />}
					onClick={() => setEditingCustomTab({})}
				/>
			</Navigation>

			{activeCustomTab && (
				<div className="enquiry-active-tag-preview">
					<span>Showing enquiries tagged with</span>
					<div className="enquiry-active-tag-list">
						{(activeCustomTab.tags || []).map((tag) => (
							<span
								key={tag}
								className="enquiry-active-tag">
								{tag}
							</span>
						))}
					</div>
				</div>
			)}

			{/* Enquries that require attention (just been submitted from the website) */}
			{user?.show_for?.can_assign && activeTab === "REQUIRE_ATTENTION" && (
				<RequireAttention
					maskDetails={user?.mask_details}
					setBulk={setBulkAssignments}
					setAssignEnquiry={setAssignEnquiry}
					startBulkAssign={() => setBulkAssigning(true)}
					updateRequiredCount={setRequireAttentionCount}
					showEnquiryWindow={openEnquiry}
					searchTerm={enquirySearch}
				/>
			)}

			{/* Returning travellers - bookings that returned home in the last ~1.5 months */}
			{activeTab === "RETURN_TRAVELLERS" && (
				<ReturnTravellers
					isAdmin={user?.is_admin || user?.is_manager}
					currentUid={uid}
					maskDetails={user?.mask_details}
					searchTerm={enquirySearch}
					updateCount={setReturnTravellersCount}
					openEnquiry={openEnquiry}
				/>
			)}

				{/* Archive tab */}
			{activeTab === "ARCHIVE" && (
				<>
					{/* Email search for archived enquiries */}
					<div className="archive-email-search-row">
						<Input
							value={archiveEmailDraft}
							icon={<SearchIcon />}
							placeholder="Search by customer email"
							onChange={(value) => setArchiveEmailDraft(value)}
							wrapperClass="archive-email-search"
							onKeyPress={(e) => {
								if (e.key === "Enter") {
									e.preventDefault()
									setArchiveEmailSearch(archiveEmailDraft)
								}
							}}
						/>
						<Button
							small={true}
							label="Search"
							onClick={() => setArchiveEmailSearch(archiveEmailDraft)}
						/>
						{archiveSearching && (
							<span style={{ fontSize: "14px", color: "#666" }}>Searching...</span>
						)}
						{(archiveEmailDraft || archiveEmailSearch) && !archiveSearching && (
							<Button
								small={true}
								label="Clear"
								onClick={() => {
									setArchiveEmailDraft("")
									setArchiveEmailSearch("")
								}}
							/>
						)}
					</div>
					<Table
						className="enquiries-table"
						headings={archiveHeadings}>
						<Archive
							searchEmail={archiveEmailSearch}
							sort={archiveSort}
							onSearchingChange={(isSearching) => setArchiveSearching(isSearching)}
						/>
					</Table>
				</>
			)}

			{/* For all other enqiury tabs that are not "REQUIRE_ATTENTION" and not "ARCHIVE" */}
			{showQueueTable && (
				<>
					{canBulkSelect && (
						<div className={["enquiry-bulk-bar", queueSelection.length === 0 && "is-disabled"].filter(Boolean).join(" ")}>
							<span className="enquiry-bulk-count">
								{queueSelection.length} {queueSelection.length === 1 ? "enquiry" : "enquiries"} selected
							</span>
							<div className="enquiry-bulk-actions">
								<Button
									small={true}
									type="OUTLINE"
									label="Clear selection"
									disabled={queueSelection.length === 0}
									onClick={() => setQueueSelection([])}
								/>
								{canBulkReassign && (
									<Button
										small={true}
										label="Reassign selected"
										disabled={queueSelection.length === 0}
										onClick={bulkReassignSelected}
									/>
								)}
								{canBulkDelete && (
									<Button
										small={true}
										className="enquiry-bulk-delete"
										label="Delete selected"
										disabled={queueSelection.length === 0}
										onClick={bulkDeleteSelected}
									/>
								)}
							</div>
						</div>
					)}

					<Table
						className="enquiries-table"
						headings={queueTableHeadings}>
						{loadingEnquiries && (
							<tr>
								<td colSpan={queueColSpan}>
									<small>Loading enquiries...</small>
								</td>
							</tr>
						)}

						{!loadingEnquiries && enquiries.map((enquiry) => (
							<Enquiry
								key={enquiry.id}
								id={enquiry.id}
								details={enquiry}
								maskDetails={user?.mask_details}
								clientData={clientsById[enquiry.client]}
								showEnquiryWindow={openEnquiry}
								isSuperAdmin={user?.is_super_admin}
								selectable={canBulkSelect}
								isSelected={queueSelection.includes(enquiry.id)}
								onToggleSelect={toggleQueueSelect}
							/>
						))}

						{!loadingEnquiries && enquiries.length === 0 && (
							<tr>
								<td colSpan={queueColSpan}>
									<small>{emptyQueueMessage}</small>
								</td>
							</tr>
						)}
					</Table>
				</>
			)}

			{/* Are we assigning an enquiry (single from a row, or the bulk selection) to an agent */}
			{(assignEnquiry || bulkAssigning) && (
				<Assign
					bulkAssign={bulkAssigning ? bulkAssignments : []}
					enquiryID={assignEnquiry}
					close={() => {
						setAssignEnquiry("")
						setBulkAssigning(false)
						setQueueSelection([])
					}}
				/>
			)}

			{/* Are we adding a new enquiry? */}
			{addingEnquiry && <Add close={() => setAddingEnquiry(false)} />}

			{editingCustomTab && (
				<CustomTabWindow
					uid={uid}
					availableTags={usedTags}
					existingTabs={customTabs}
					editingTab={editingCustomTab.id ? editingCustomTab : null}
					tabCount={customTabs.length}
					close={() => setEditingCustomTab(null)}
					onSaved={(tabID) => {
						setActiveTab(`${CUSTOM_TAB_PREFIX}${tabID}`)
						setEditingCustomTab(null)
					}}
					onDeleted={(tabID) => {
						if (activeTab === `${CUSTOM_TAB_PREFIX}${tabID}`) {
							setActiveTab("BEING_PROCESSED")
						}
						setEditingCustomTab(null)
					}}
				/>
			)}
		</Tile>
	)
}

export default Enquiries
